import { Vehicle } from "../../../api/vehiclesApi";

export type LabelPosition =
  | "top"
  | "topRight"
  | "right"
  | "bottomRight"
  | "bottom"
  | "bottomLeft"
  | "left"
  | "topLeft";

export interface CreateMarkerElementOptions {
  vehicle: Vehicle;
  isSelected: boolean;
  labelPosition?: LabelPosition;
  isDarkMode?: boolean;
}

export const createShopMarkerElement = (): HTMLElement => {
  const markerElement = document.createElement("div");
  markerElement.className = "shop-marker";
  markerElement.innerHTML = `
    <div style="
      width: 16px;
      height: 16px;
      background-color: #FFD700;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 10px;
      box-shadow: 0 1px 2px rgba(0,0,0,0.2);
    ">
      S
    </div>
  `;
  return markerElement;
};

export interface CreateVehicleIconOptions {
  speed: number | null;
  heading: number | null;
  isSelected?: boolean;
  isDarkMode?: boolean;
  isUnplugged?: boolean;
}

/**
 * Creates just the icon element (arrow or square) for a vehicle marker.
 * Reusable across the fleet map and the public tracking page.
 */
export const createVehicleIconElement = ({
  speed,
  heading,
  isSelected = false,
  isDarkMode = false,
  isUnplugged = false,
}: CreateVehicleIconOptions): HTMLElement => {
  const iconWrapper = document.createElement("div");
  iconWrapper.style.position = "relative";
  iconWrapper.style.display = "flex";
  iconWrapper.style.alignItems = "center";
  iconWrapper.style.justifyContent = "center";

  const iconContainer = document.createElement("div");
  iconContainer.style.position = "relative";
  const baseSize = isUnplugged ? 16 : isSelected ? 18 : 12;
  const borderSize = isSelected ? 3 : isUnplugged ? 2 : 1;
  iconContainer.style.width = `${baseSize}px`;
  iconContainer.style.height = `${baseSize}px`;
  iconContainer.style.border = `${borderSize}px solid ${isSelected ? "#FFC107" : isUnplugged ? "#d32f2f" : "white"}`;
  iconContainer.style.backgroundColor = isUnplugged
    ? "#f44336"
    : (speed || 0) > 0
      ? isDarkMode
        ? "#4a90e2"
        : "#0b264c"
      : isDarkMode
      ? "#9e9e9e"
      : "#636067";

  if ((speed || 0) === 0) {
    iconContainer.style.borderRadius = "2px";
  } else {
    iconContainer.style.borderRadius = "0";
    iconContainer.style.clipPath = "polygon(50% 0%, 100% 100%, 0% 100%)";
    iconContainer.style.transform = `rotate(${heading || 0}deg)`;
  }

  if (isUnplugged && !isSelected) {
    iconContainer.style.boxShadow = "0 0 8px rgba(244, 67, 54, 0.7)";
  }

  if (isSelected) {
    iconWrapper.style.width = `${baseSize + 10}px`;
    iconWrapper.style.height = `${baseSize + 10}px`;

    const ring = document.createElement("div");
    ring.style.position = "absolute";
    ring.style.inset = "0";
    ring.style.borderRadius = "50%";
    ring.style.border = `2px solid ${isUnplugged ? "rgba(244,67,54,0.9)" : "rgba(255,193,7,0.9)"}`;
    ring.style.boxShadow = `0 0 0 4px ${isUnplugged ? "rgba(244,67,54,0.25)" : "rgba(255,193,7,0.25)"}`;
    iconWrapper.appendChild(ring);
  }

  iconWrapper.appendChild(iconContainer);
  return iconWrapper;
};

export const createMarkerElement = ({
  vehicle,
  isSelected,
  labelPosition = "top",
  isDarkMode = false,
}: CreateMarkerElementOptions): HTMLElement => {
  const markerElement = document.createElement("div");
  markerElement.style.position = "relative";
  markerElement.style.display = "flex";
  markerElement.style.flexDirection = "column";
  markerElement.style.alignItems = "center";

  const label = document.createElement("div");
  label.textContent = vehicle.name;
  label.style.position = "absolute";
  label.style.backgroundColor = isDarkMode
    ? "rgba(33, 33, 33, 0.9)"
    : "rgba(255, 255, 255, 0.9)";
  label.style.color = isDarkMode ? "#fff" : "#000";
  label.style.padding = "2px 4px";
  label.style.borderRadius = "4px";
  label.style.fontSize = "10px";
  label.style.border = isDarkMode
    ? "1px solid rgba(255,255,255,0.2)"
    : "1px solid rgba(0,0,0,0.1)";
  label.style.whiteSpace = "nowrap";
  label.style.zIndex = "1";
  label.style.maxWidth = "80px";
  label.style.overflow = "hidden";
  label.style.textOverflow = "ellipsis";
  label.style.fontFamily = "Roboto, sans-serif";
  label.style.fontWeight = "500";
  label.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";

  if (!isSelected) {
    switch (labelPosition) {
      case "topLeft":
        label.style.bottom = "100%";
        label.style.right = "100%";
        label.style.marginRight = "2px";
        label.style.marginBottom = "-8px";
        break;
      case "top":
        label.style.bottom = "100%";
        label.style.left = "50%";
        label.style.transform = "translateX(-50%)";
        label.style.marginBottom = "2px";
        break;
      case "topRight":
        label.style.bottom = "100%";
        label.style.left = "100%";
        label.style.marginLeft = "2px";
        label.style.marginBottom = "-8px";
        break;
      case "right":
        label.style.left = "100%";
        label.style.top = "50%";
        label.style.transform = "translateY(-50%)";
        label.style.marginLeft = "4px";
        break;
      case "bottomRight":
        label.style.top = "100%";
        label.style.left = "100%";
        label.style.marginLeft = "2px";
        label.style.marginTop = "-8px";
        break;
      case "bottom":
        label.style.top = "100%";
        label.style.left = "50%";
        label.style.transform = "translateX(-50%)";
        label.style.marginTop = "2px";
        break;
      case "bottomLeft":
        label.style.top = "100%";
        label.style.right = "100%";
        label.style.marginRight = "2px";
        label.style.marginTop = "-8px";
        break;
      case "left":
        label.style.right = "100%";
        label.style.top = "50%";
        label.style.transform = "translateY(-50%)";
        label.style.marginRight = "4px";
        break;
    }
  }

  const isUnplugged = vehicle.gatewayHealthStatus === "Unplugged";

  if (isUnplugged) {
    label.style.backgroundColor = "rgba(244, 67, 54, 0.95)";
    label.style.color = "#fff";
    label.style.border = "1px solid #d32f2f";
    label.style.fontWeight = "700";
  }

  const iconWrapper = createVehicleIconElement({
    speed: vehicle.lastSpeed,
    heading: vehicle.lastHeading,
    isSelected,
    isDarkMode,
    isUnplugged,
  });

  if (!isSelected) {
    markerElement.appendChild(label);
  }
  markerElement.appendChild(iconWrapper);

  return markerElement;
};
